import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { Sun, Moon, Monitor } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";

/**
 * Theme toggle button (sun/moon/monitor) for the topbar.
 * - Single button that animates between sun & moon.
 * - Click → opens menu with Light / Dark / System.
 */
export function ThemeToggle({ compact = false }) {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  // Avoid hydration mismatch — render placeholder until client mount
  useEffect(() => setMounted(true), []);

  if (!mounted) {
    return (
      <button
        type="button"
        className="inline-flex items-center justify-center rounded-lg w-9 h-9 text-muted-foreground"
        aria-label="Cambiar tema"
        data-testid="theme-toggle"
      >
        <Sun className="w-4 h-4" />
      </button>
    );
  }

  const isDark = resolvedTheme === "dark";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="relative inline-flex items-center justify-center rounded-lg w-9 h-9 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Cambiar tema"
          data-testid="theme-toggle"
          data-compact="true"
        >
          <Sun
            className={`absolute w-4 h-4 transition-all duration-300 ${
              isDark ? "opacity-0 rotate-90 scale-50" : "opacity-100 rotate-0 scale-100"
            }`}
          />
          <Moon
            className={`absolute w-4 h-4 transition-all duration-300 ${
              isDark ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-50"
            }`}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44" data-testid="theme-toggle-menu">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
          Apariencia
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={theme} onValueChange={setTheme}>
          <DropdownMenuRadioItem value="light" className="cursor-pointer" data-testid="theme-option-light">
            <Sun className="w-4 h-4 mr-2" />
            Claro
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark" className="cursor-pointer" data-testid="theme-option-dark">
            <Moon className="w-4 h-4 mr-2" />
            Oscuro
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system" className="cursor-pointer" data-testid="theme-option-system">
            <Monitor className="w-4 h-4 mr-2" />
            Sistema
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ThemeToggle;
